import type { GameState } from '../../engine/game-state.js';
import { createLayout, rowAbove } from '../../render/three/layout.js';
import type { GameSpec } from '../../render/three/game-app.js';
import { drawFromStock, hasAnyNonStockMove, klondikeRules } from './rules.js';

const TABLEAU_IDS = ['t1', 't2', 't3', 't4', 't5', 't6', 't7'] as const;
const FOUNDATION_IDS = ['f-spades', 'f-hearts', 'f-diamonds', 'f-clubs'] as const;

const TABLEAU_Z = 0.0;
const TOP_Z = rowAbove(TABLEAU_Z);

const layout = createLayout({
  cols: 7,
  rows: [
    { z: TOP_Z, slots: ['stock', 'waste', null, ...FOUNDATION_IDS] },
    { z: TABLEAU_Z, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function dragStackIds(state: GameState, cardId: string): string[] {
  for (const pile of Object.values(state.piles)) {
    const idx = pile.cards.findIndex((c) => c.id === cardId);
    if (idx === -1) continue;
    const head = pile.cards[idx]!;
    if (!head.faceUp) return [];
    if (pile.type === 'stock') return [];
    if (pile.type !== 'tableau') {
      if (idx !== pile.cards.length - 1) return [];
      return [cardId];
    }
    return pile.cards.slice(idx).map((c) => c.id);
  }
  return [];
}

export interface KlondikeSpecOptions {
  drawCount?: number;
}

const WASTE_FAN_DX = 0.32;

export function makeKlondikeSpec(opts: KlondikeSpecOptions = {}): GameSpec {
  const drawCount = opts.drawCount ?? 1;
  const fanWaste = drawCount > 1;
  return {
    rules: klondikeRules,
    slotForPile: (id) => layout.slot(id),
    dropExclude: () => ['stock'],
    dragStackIds,
    autoSendOrder: () => [...FOUNDATION_IDS, ...TABLEAU_IDS],
    // Worst case: a 13-card descending K-A run rebuilt on a single column.
    maxFanLength: 13,
    cardOffset(pile, i) {
      if (!fanWaste || pile.id !== 'waste') return { dx: 0, dz: 0 };
      const n = pile.cards.length;
      const startIdx = Math.max(0, n - drawCount);
      if (i < startIdx) return { dx: 0, dz: 0 };
      return { dx: (i - startIdx) * WASTE_FAN_DX, dz: 0 };
    },
    onPileClick(state, pile) {
      if (pile.id === 'stock') {
        drawFromStock(state, drawCount);
        return true;
      }
      if (pile.id === 'waste' && state.piles.stock!.cards.length === 0) {
        drawFromStock(state, drawCount);
        return { snap: true };
      }
      return false;
    },
    statusText(state) {
      const stockEmpty = state.piles.stock!.cards.length === 0;
      const wasteEmpty = state.piles.waste!.cards.length === 0;
      if (stockEmpty && wasteEmpty && !hasAnyNonStockMove(state)) return 'No moves left';
      return null;
    },
  };
}

export const klondikeSpec: GameSpec = makeKlondikeSpec({ drawCount: 1 });
